import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/Card';
import { Toast } from '../components/ui/Toast';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { listResortAdminUsers, createResortAdminUser, updateResortAdminUser, getResortLocations, listRestaurants, listGroceries, listResortAdminRoles, resendPasswordToken } from '../services/resortAdminApi';
import { UserPlus, KeyIcon, Edit2, Mail } from 'lucide-react';

export const Settings: React.FC = () => {
  const navigate = useNavigate();
  const {
    user,
    changePassword
  } = useAuth();
  const isSuperAdmin = user?.role === 'Resort Super Admin';
  const canManageUsers = isSuperAdmin; // Only Super Admin can create/manage users
  const [activeTab, setActiveTab] = useState<'users' | 'adduser' | 'password'>(canManageUsers ? 'users' : 'password');
  
  // Resort Admin Users state
  const [resortAdminUsers, setResortAdminUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [, setResortLocations] = useState<Array<{ value: string; label: string }>>([]);
  const [restaurants, setRestaurants] = useState<Array<{ name: string; restaurant_name: string; click_and_collect?: number }>>([]);
  const [groceries, setGroceries] = useState<Array<{ name: string; grocery_name: string; click_and_collect?: number }>>([]);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [allRoles, setAllRoles] = useState<string[]>([]);
  const [resendingToken, setResendingToken] = useState<string | null>(null);

  // User management states
  const [newUserForm, setNewUserForm] = useState({
    email: '',
    role: 'Partner Admin',
    scope_type: 'all' as 'restaurant' | 'grocery' | 'all',
    restaurants: [] as string[],
    groceries: [] as string[],
  });
  const [userError, setUserError] = useState('');
  const [userSuccess, setUserSuccess] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Password change states
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Load resort locations, restaurants, groceries, and roles
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load resort locations
        const locationsResult = await getResortLocations();
        if (locationsResult && locationsResult.resort_locations) {
          setResortLocations(locationsResult.resort_locations);
        }

        // Load restaurants
        const restaurantsResult = await listRestaurants(undefined, 1, 1000); // Get all restaurants
        if (restaurantsResult && restaurantsResult.data) {
          setRestaurants(restaurantsResult.data);
        }

        // Load groceries
        const groceriesResult = await listGroceries(undefined, 1, 1000); // Get all groceries
        if (groceriesResult && groceriesResult.data) {
          setGroceries(groceriesResult.data);
        }

        // Load available roles from server (already excludes "Resort Super Admin")
        const rolesResult = await listResortAdminRoles();
        if (rolesResult && Array.isArray(rolesResult)) {
          // All roles from server (already excludes "Resort Super Admin")
          // This includes: Partner Admin, Partner Courier, Support
          setAllRoles(rolesResult);
          // Use all roles for both create and edit dropdowns
          setAvailableRoles(rolesResult);
          // Set default role if available
          if (rolesResult.length > 0 && !rolesResult.includes(newUserForm.role)) {
            setNewUserForm({ ...newUserForm, role: rolesResult[0] });
          }
        } else {
          console.error('Failed to load roles or invalid format:', rolesResult);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, []);

  // Load Resort admin users (only for Super Admin)
  const loadResortAdminUsers = async () => {
    if (!canManageUsers) return;
    setLoadingUsers(true);
    try {
      const result = await listResortAdminUsers();
      if (result && result.data) {
        setResortAdminUsers(result.data);
      }
    } catch (error) {
      console.error('Error loading Resort admin users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Load users on component mount (only for Super Admin)
  useEffect(() => {
    if (canManageUsers) {
      loadResortAdminUsers();
    }
  }, [canManageUsers]);

  // Redirect non-super-admins away from users tab
  useEffect(() => {
    if (!canManageUsers && (activeTab === 'users' || activeTab === 'adduser')) {
      setActiveTab('password');
    }
  }, [canManageUsers, activeTab]);

  // Handle resending password token
  const handleResendToken = async (userName: string) => {
    setResendingToken(userName);
    setUserError('');
    setUserSuccess('');
    try {
      await resendPasswordToken(userName);
      setUserSuccess('Password reset link has been resent successfully.');
      await loadResortAdminUsers();
    } catch (error: any) {
      setUserError(error.message || 'Failed to resend password token');
    } finally {
      setResendingToken(null);
    }
  };

  // Handle adding a new user
  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setUserError('');
    setUserSuccess('');
    if (!newUserForm.email) {
      setUserError('Email is required');
      return;
    }
    if (newUserForm.role !== 'Support' && newUserForm.scope_type === 'restaurant' && newUserForm.restaurants.length === 0) {
      setUserError('At least one restaurant is required');
      return;
    }
    if (newUserForm.role !== 'Support' && newUserForm.scope_type === 'grocery' && newUserForm.groceries.length === 0) {
      setUserError('At least one grocery is required');
      return;
    }
    if (newUserForm.role !== 'Support' && newUserForm.scope_type === 'all' && newUserForm.restaurants.length === 0 && newUserForm.groceries.length === 0) {
      setUserError('At least one restaurant or grocery is required');
      return;
    }
    try {
      await createResortAdminUser({
        email: newUserForm.email,
        role: newUserForm.role,
        scope_type: newUserForm.scope_type as 'restaurant' | 'grocery',
        restaurants: newUserForm.role !== 'Support' && (newUserForm.scope_type === 'restaurant' || newUserForm.scope_type === 'all') ? newUserForm.restaurants : undefined,
        groceries: newUserForm.role !== 'Support' && (newUserForm.scope_type === 'grocery' || newUserForm.scope_type === 'all') ? newUserForm.groceries : undefined,
      });
      const msg = 'User added successfully. The user will receive an email to set their password.';
      setUserSuccess(msg);
      setToast({ message: msg, type: 'success' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setNewUserForm({
        email: '',
        role: 'Partner Admin',
        scope_type: 'all',
        restaurants: [],
        groceries: [],
      });
      // Reload users list
      await loadResortAdminUsers();
    } catch (error: any) {
      const errMsg = error.message || 'Failed to add user. Email may already be in use.';
      setUserError(errMsg);
      setToast({ message: errMsg, type: 'error' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle deleting a user

  // Handle changing user role
  const handleRoleChange = async (email: string, role: string) => {
    setUserError('');
    setUserSuccess('');
    try {
      await updateResortAdminUser({
        name: email,
        role: role as any
      });
      setUserSuccess('User role updated successfully');
      setToast({ message: 'User role updated successfully', type: 'success' });
      await loadResortAdminUsers();
    } catch (error) {
      setUserError('Failed to update user role');
      setToast({ message: 'Failed to update user role', type: 'error' });
    }
  };

  // Handle password change
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }
    const success = await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
    if (success) {
      setPasswordSuccess('Password changed successfully');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } else {
      setPasswordError('Current password is incorrect');
    }
  };

  return (
    <div className="space-y-6">
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
      <div>
        <h1 className="text-2xl font-semibold text-gray-800">Settings</h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage application settings
        </p>
      </div>
      {/* Tabs */}
      <div className="flex space-x-4 border-b border-gray-200">
        {canManageUsers && (
          <button
            onClick={() => setActiveTab('users')}
            className={`py-2 px-4 ${
              activeTab === 'users'
                ? 'border-b-2 border-alps-primary text-alps-primary'
                : 'text-gray-500'
            }`}
          >
          User Management
        </button>
        )}
        {canManageUsers && (
          <button
            onClick={() => setActiveTab('adduser')}
            className={`py-2 px-4 ${
              activeTab === 'adduser'
                ? 'border-b-2 border-alps-primary text-alps-primary'
                : 'text-gray-500'
            }`}
          >
          Add User
        </button>
        )}
        <button
          onClick={() => setActiveTab('password')}
          className={`py-2 px-4 ${
            activeTab === 'password'
              ? 'border-b-2 border-alps-primary text-alps-primary'
              : 'text-gray-500'
          }`}
        >
          Change Password
        </button>
      </div>
      {/* User Management Tab */}
      {activeTab === 'users' && canManageUsers && (
        <div className="space-y-6">
          <Card title="Manage Users">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Email
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Role
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Scope
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loadingUsers ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                        Loading users...
                      </td>
                    </tr>
                  ) : resortAdminUsers.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    resortAdminUsers.map((u) => (
                      <tr key={u.name}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {u.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {u.email === user?.email ? (
                            <span className="capitalize">{u.role}</span>
                          ) : (
                            <select
                              value={u.role}
                              onChange={(e) =>
                                handleRoleChange(u.email, e.target.value)
                              }
                              className="border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                            >
                              {allRoles.length > 0 ? (
                                allRoles.map((role) => (
                                  <option key={role} value={role}>
                                    {role}
                                  </option>
                                ))
                              ) : (
                                <>
                                  <option value={u.role}>{u.role}</option>
                                  <option value="Partner Admin">Partner Admin</option>
                                  <option value="Partner Courier">Partner Courier</option>
                                  <option value="Support">Support</option>
                                </>
                              )}
                            </select>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          <div className="space-y-1">
                            {u.restaurant && (
                              <div>Restaurants: {u.restaurant}</div>
                            )}
                            {u.grocery && (
                              <div>Groceries: {u.grocery}</div>
                            )}
                            {!u.restaurant && !u.grocery && '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              u.status === 'Active'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {u.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => navigate(`/settings/users/${u.name}`)}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="Edit user"
                            >
                              <Edit2 className="h-5 w-5" />
                            </button>
                            {u.has_expired_token && u.status === 'Disabled' && (
                              <button
                                onClick={() => handleResendToken(u.name)}
                                disabled={resendingToken === u.name}
                                className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                                title="Resend password link"
                              >
                                <Mail className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
          
        </div>
      )}
      {/* Add User Tab */}
      {activeTab === 'adduser' && canManageUsers && (
        <Card title="Add Resort Admin User">
          {userError && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-red-700">{userError}</p>
                </div>
              </div>
            </div>
          )}
          {userSuccess && (
            <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-green-700">{userSuccess}</p>
                </div>
              </div>
            </div>
          )}
          <form onSubmit={handleAddUser} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                id="email"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                value={newUserForm.email}
                onChange={(e) =>
                  setNewUserForm({ ...newUserForm, email: e.target.value })
                }
                required
              />
            </div>
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                Role
              </label>
              <select
                id="role"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                value={newUserForm.role}
                onChange={(e) => {
                  const selectedRole = e.target.value;
                  // Automatically set scope_type based on role
                  let newScopeType: 'restaurant' | 'grocery' | 'all' = newUserForm.scope_type;
                  let newRestaurants = newUserForm.restaurants;
                  let newGroceries = newUserForm.groceries;
                  if (selectedRole === 'Partner Admin' || selectedRole === 'Partner Courier' || selectedRole === 'Resort Super Admin' || selectedRole === 'Support') {
                    newScopeType = 'all';
                  }
                  setNewUserForm({
                    ...newUserForm,
                    role: selectedRole,
                    scope_type: newScopeType,
                    restaurants: newRestaurants,
                    groceries: newGroceries,
                  });
                }}
              >
                {availableRoles.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="scope_type" className="block text-sm font-medium text-gray-700">
                Scope Type
              </label>
              <select
                id="scope_type"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                value={newUserForm.scope_type}
                onChange={(e) =>
                  setNewUserForm({
                    ...newUserForm,
                    scope_type: e.target.value as 'restaurant' | 'grocery' | 'all',
                    restaurants: e.target.value === 'grocery' ? [] : newUserForm.restaurants,
                    groceries: e.target.value === 'restaurant' ? [] : newUserForm.groceries,
                  })
                }
                disabled={newUserForm.role === 'Partner Admin' || newUserForm.role === 'Partner Courier' || newUserForm.role === 'Resort Super Admin' || newUserForm.role === 'Support'}
              >
                <option value="restaurant">Restaurant</option>
                <option value="grocery">Grocery</option>
                {(newUserForm.role === 'Resort Super Admin' || newUserForm.role === 'Partner Admin' || newUserForm.role === 'Partner Courier' || newUserForm.role === 'Support') && (
                  <option value="all">All (Restaurants & Groceries)</option>
                )}
              </select>
              {(newUserForm.role === 'Partner Admin' || newUserForm.role === 'Partner Courier' || newUserForm.role === 'Resort Super Admin' || newUserForm.role === 'Support') && (
                <p className="mt-1 text-xs text-gray-500">Scope type is automatically set based on the selected role</p>
              )}
            </div>
            {(newUserForm.scope_type === 'restaurant' || newUserForm.scope_type === 'all') && newUserForm.role !== 'Support' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Restaurants (you can select multiple) <span className="text-red-500">*</span>
                </label>
                <div className="mt-1 border border-gray-300 rounded-md shadow-sm max-h-48 overflow-y-auto p-2">
                  {restaurants
                    .filter((restaurant) => {
                      // Filter out C&C entities when courier role is selected
                      if (newUserForm.role === 'Partner Courier') {
                        return !restaurant.click_and_collect;
                      }
                      return true;
                    })
                    .map((restaurant) => (
                    <label key={restaurant.name} className="flex items-center py-1 px-2 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        className="mr-2 h-4 w-4 text-alps-primary focus:ring-alps-primary border-gray-300 rounded"
                        checked={newUserForm.restaurants.includes(restaurant.name)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewUserForm({
                              ...newUserForm,
                              restaurants: [...newUserForm.restaurants, restaurant.name],
                            });
                          } else {
                            setNewUserForm({
                              ...newUserForm,
                              restaurants: newUserForm.restaurants.filter((r) => r !== restaurant.name),
                            });
                          }
                        }}
                      />
                      <span className="text-sm text-gray-700">
                        {restaurant.restaurant_name || restaurant.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {(newUserForm.scope_type === 'grocery' || newUserForm.scope_type === 'all') && newUserForm.role !== 'Support' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Groceries (you can select multiple) <span className="text-red-500">*</span>
                </label>
                <div className="mt-1 border border-gray-300 rounded-md shadow-sm max-h-48 overflow-y-auto p-2">
                  {groceries
                    .filter((grocery) => {
                      // Filter out C&C entities when courier role is selected
                      if (newUserForm.role === 'Partner Courier') {
                        return !grocery.click_and_collect;
                      }
                      return true;
                    })
                    .map((grocery) => (
                    <label key={grocery.name} className="flex items-center py-1 px-2 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        className="mr-2 h-4 w-4 text-alps-primary focus:ring-alps-primary border-gray-300 rounded"
                        checked={newUserForm.groceries.includes(grocery.name)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewUserForm({
                              ...newUserForm,
                              groceries: [...newUserForm.groceries, grocery.name],
                            });
                          } else {
                            setNewUserForm({
                              ...newUserForm,
                              groceries: newUserForm.groceries.filter((g) => g !== grocery.name),
                            });
                          }
                        }}
                      />
                      <span className="text-sm text-gray-700">
                        {grocery.grocery_name || grocery.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div>
              <button
                type="submit"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-alps-primary hover:bg-alps-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-alps-primary"
              >
                <UserPlus className="mr-2 h-4 w-4" />
                Add User
              </button>
            </div>
          </form>
        </Card>
      )}
      {/* Change Password Tab */}
      {activeTab === 'password' && (
        <Card title="Change Password">
          {passwordError && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-red-700">{passwordError}</p>
                </div>
              </div>
            </div>
          )}
          {passwordSuccess && (
            <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-green-700">{passwordSuccess}</p>
                </div>
              </div>
            </div>
          )}
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label
                htmlFor="currentPassword"
                className="block text-sm font-medium text-gray-700"
              >
                Current Password
              </label>
              <input
                type="password"
                id="currentPassword"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                value={passwordForm.currentPassword}
                onChange={(e) =>
                  setPasswordForm({
            ...passwordForm,
            currentPassword: e.target.value
                  })
                }
                required
              />
            </div>
            <div>
              <label
                htmlFor="newPassword"
                className="block text-sm font-medium text-gray-700"
              >
                New Password
              </label>
              <input
                type="password"
                id="newPassword"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                value={passwordForm.newPassword}
                onChange={(e) =>
                  setPasswordForm({
            ...passwordForm,
            newPassword: e.target.value
                  })
                }
                required
              />
            </div>
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700"
              >
                Confirm New Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                value={passwordForm.confirmPassword}
                onChange={(e) =>
                  setPasswordForm({
            ...passwordForm,
            confirmPassword: e.target.value
                  })
                }
                required
              />
            </div>
            <div>
              <button
                type="submit"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-alps-primary hover:bg-alps-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-alps-primary"
              >
                <KeyIcon className="mr-2 h-4 w-4" />
                Change Password
              </button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
};
